// src/pages/PatientLoginPage.jsx
import React, { useState, useEffect } from 'react';
import axiosInstance from '../axiosInstance';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useGuest } from '../context/GuestContext';
import { 
  Avatar, 
  Button, 
  TextField, 
  Link, 
  Box, 
  Typography, 
  Paper, 
  CssBaseline, 
  CircularProgress, 
  Fade,
  Divider,
  useMediaQuery,
  useTheme,
  alpha
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import logo from '../assets/tu_logo.png';
import backgroundImage from '../assets/login.jpeg';

const PatientLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isVerySmall = useMediaQuery('(max-height: 600px)');
  const isExtraSmall = useMediaQuery('(max-height: 500px)');
  const { loginAsGuest } = useGuest();
  const [identifier, setIdentifier] = useState('');
 


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      localStorage.removeItem('isGuest');
      localStorage.removeItem('guestToken');

      const response = await axiosInstance.post('/patients/login/', {
        identifier: (identifier || '').trim(),
        password,
      });

      // Guarda token de paciente y elimina tokens que puedan interferir
      localStorage.setItem('patientToken', response.data.token);
      localStorage.removeItem('authToken'); // <-- evita que axios use token de médico

      toast.success('¡Has iniciado sesión correctamente!');
      navigate('/patient/dashboard');
    } catch (err) {
      setError('El correo/usuario o la contraseña son incorrectos.');
      toast.error('Error al iniciar sesión.');
    } finally {
      setLoading(false);
    }
  };

  const handleGuestLogin = () => {
    setLoading(true);

    localStorage.removeItem('authToken');    // token de médico
    localStorage.removeItem('patientToken'); // token de paciente

    loginAsGuest();
    toast.info('Accediendo como invitado. Algunas funciones estarán limitadas.');
    
    navigate('/doctors'); // <- ruta pública
    setLoading(false);
  };
  

  // Ajustar tamaños basados en el tamaño de pantalla
  const getResponsiveSizes = () => {
    if (isExtraSmall) {
      return {
        logoHeight: '40px',
        avatarSize: 24,
        iconSize: 'small',
        titleVariant: 'body1',
        inputSize: 'small',
        buttonPadding: 0.5,
        spacing: 0.5,
        linkVariant: 'caption',
        helperTextMargin: 0
      };
    } else if (isVerySmall) {
      return {
        logoHeight: '60px',
        avatarSize: 32,
        iconSize: 'small',
        titleVariant: 'subtitle1',
        inputSize: 'small',
        buttonPadding: 0.8,
        spacing: 1,
        linkVariant: 'caption',
        helperTextMargin: 0
      };
    } else {
      return {
        logoHeight: isMobile ? '80px' : '120px',
        avatarSize: 40,
        iconSize: 'medium',
        titleVariant: 'h5',
        inputSize: 'medium',
        buttonPadding: 1.5,
        spacing: 2,
        linkVariant: 'body2',
        helperTextMargin: 1
      };
    }
  };

  const sizes = getResponsiveSizes();

  // Overlay para modo oscuro
  const darkOverlay = isDarkMode ? {
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.6)', // Overlay oscuro para mejorar contraste
      zIndex: 1
    }
  } : {};

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        overflow: 'hidden', // Evita scroll en el contenedor principal
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        ...darkOverlay // Aplicar overlay en modo oscuro
      }}
    >
      <CssBaseline />
      <Fade in={true} timeout={1000}>
        <Box
          sx={{ 
            width: { xs: '90%', sm: '80%', md: '50%', lg: '40%' },
            maxWidth: '500px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            zIndex: 2 // Por encima del overlay
          }}
        >
          <Paper
            elevation={0} // Material 3 prefiere menos sombras, el efecto lo da el color y el borde
            sx={{ 
              width: '100%',
              p: { 
                xs: isExtraSmall ? 1 : isVerySmall ? 1.5 : 2, 
                sm: isVerySmall ? 2 : 3, 
                md: 4 
              }, 
              borderRadius: '16px', // Bordes redondeados pero no demasiado
              backgroundColor: isDarkMode 
                ? alpha(theme.palette.background.paper, 0.85) // Fondo semi-transparente en modo oscuro
                : 'rgba(255, 255, 255, 0.95)',  
              backdropFilter: 'blur(10px)',
              boxShadow: theme.shadows[12], // Sombra más pronunciada para destacar
              border: `1px solid ${isDarkMode ? alpha(theme.palette.divider, 0.2) : alpha(theme.palette.divider, 0.4)}`,
              overflow: 'hidden',
              // Forma más rectangular
              aspectRatio: 'auto', // Eliminar restricción de aspecto
              maxHeight: 'none', // Eliminar altura máxima
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              width: '100%'
            }}>
              {!isExtraSmall && (
                <img 
                  src={logo} 
                  alt="Logo" 
                  style={{ 
                    marginBottom: sizes.spacing / 2, 
                    maxHeight: sizes.logoHeight,
                    objectFit: 'contain',
                    filter: isDarkMode ? 'brightness(0.9) contrast(1.1)' : 'none'
                  }} 
                />
              )}
              
              <Avatar sx={{ 
                m: sizes.spacing / 2, 
                bgcolor: 'primary.main',
                width: sizes.avatarSize,
                height: sizes.avatarSize
              }}>
                <PersonIcon fontSize={sizes.iconSize} />
              </Avatar>
              
              <Typography 
                component="h1" 
                variant={sizes.titleVariant} 
                fontWeight="bold"
                sx={{ mb: sizes.spacing / 2 }}
              >
                Acceso de Pacientes
              </Typography>
              
              <Box 
                component="form" 
                noValidate 
                onSubmit={handleSubmit} 
                sx={{ width: '100%', mt: 1 }}
              >
                <TextField
                  required
                  fullWidth
                  label="Correo o Usuario"
                  name="identifier"
                  value={identifier}               
                  onChange={(e) => setIdentifier(e.target.value)}
                  error={!!error}
                  autoComplete="username"
                  sx={{ mb: sizes.spacing / 2 }}
                  size={sizes.inputSize}
                  margin="dense"
                />
                
                <TextField
                  required
                  fullWidth
                  name="password"
                  label="Contraseña"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={!!error}
                  helperText={error || ' '}
                  autoComplete="current-password"
                  sx={{ 
                    '& .MuiFormHelperText-root': {
                      mt: sizes.helperTextMargin,
                      mb: 0
                    }
                  }}
                  size={sizes.inputSize}
                  margin="dense"
                />
                
                <Button 
                  type="submit" 
                  fullWidth 
                  variant="contained" 
                  disabled={loading}
                  sx={{ 
                    mt: sizes.spacing, 
                    mb: sizes.spacing / 2,
                    py: sizes.buttonPadding,
                  }}
                  size={sizes.inputSize}
                >
                  {loading ? <CircularProgress size={sizes.avatarSize * 0.6} color="inherit" /> : 'Iniciar Sesión'}
                </Button>
                
                <Button
                  fullWidth
                  variant="contained" // Cambiado a contained para usar color personalizado
                  color="success" // Color verde
                  component={RouterLink}
                  to="/patient/register"
                  sx={{ 
                    mb: sizes.spacing / 2,
                    py: sizes.buttonPadding,
                    bgcolor: '#4caf50', // Verde específico
                    '&:hover': {
                      bgcolor: '#388e3c', // Verde más oscuro al hover
                    }
                  }}
                  size={sizes.inputSize}
                >
                  {isExtraSmall ? 'Regístrate' : '¿No tienes una cuenta? Regístrate'}
                </Button>
                
                {/* Botón de acceso como invitado */}
                <Button
                  fullWidth
                  variant="outline"
                  startIcon={<PersonOutlineIcon />}
                  onClick={(e) => {
                    e.preventDefault();
                    handleGuestLogin();
                  }}
                  type="button"   
                  disabled={loading}
                  sx={{ 
                    mb: sizes.spacing,
                    py: sizes.buttonPadding,
                    borderColor: theme.palette.info.main,
                    color: theme.palette.info.main,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.info.main, 0.1),
                      borderColor: theme.palette.info.dark,
                    }
                  }}
                  size={sizes.inputSize}
                >
                  {isExtraSmall ? 'Invitado' : 'Continuar como Invitado'}
                </Button>
                
                <Divider sx={{ my: sizes.spacing / 2 }}>
                  <Typography variant={sizes.linkVariant} color="text.secondary">
                    O
                  </Typography>
                </Divider>

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ArrowBackIcon fontSize={sizes.iconSize} />}
                  component={RouterLink}
                  to="/login"
                  sx={{ 
                    mt: sizes.spacing / 2,
                    mb: sizes.spacing / 2,
                    py: sizes.buttonPadding,
                  }}
                  size={sizes.inputSize}
                >
                  {isVerySmall ? 'Médicos' : 'Acceso para Médicos'}
                </Button>
                
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center',
                  mt: sizes.spacing
                }}>
                  <Link 
                    component={RouterLink} 
                    to="/patient/forgot-password" 
                    variant={sizes.linkVariant}
                    sx={{ 
                      textAlign: 'center',
                      fontWeight: 'bold',
                      fontSize: isExtraSmall ? '0.7rem' : undefined
                    }}
                  >
                    ¿Olvidaste tu contraseña?
                  </Link>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Fade>
    </Box>
  );
};

export default PatientLoginPage;
